import type {
  Note,
  Reminder,
  ReminderList,
  Todo,
  TodoList,
  UpdateFolderBody,
  UpdateNoteBody,
  UpdateReminderBody,
  UpdateTodoBody,
} from '@kanto/shared';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { clearOutbox } from '../lib/outbox';
import { api } from './client';

export const queryKeys = {
  me: ['me'] as const,
  setupState: ['setup-state'] as const,
  todos: ['todos'] as const,
  reminders: ['reminders'] as const,
  notes: ['notes'] as const,
  /**
   * Deliberately not `['notes', id]`. TanStack Query matches keys by prefix, so
   * a note nested under the list key would be refetched every time the list was
   * invalidated — which autosaving a note does, several times a minute, to
   * refresh an excerpt the editor is not showing.
   */
  note: (id: string) => ['note', id] as const,
  folders: ['folders'] as const,
  tags: ['tags'] as const,
  search: (query: string) => ['search', query] as const,
  pushKey: ['push-key'] as const,
};

/**
 * Invalidates a list, and the tag registry with it.
 *
 * The registry is derived from the modules — the names that exist and how many
 * live rows carry each — so a write that could have created a tag or moved a
 * count has to reach it. It is deliberately **not** called after ticking a box:
 * a done row is still a live row, nothing there can change a count, and the
 * most-used interaction on the screen should not cost a second request.
 */
function invalidateWithTags(client: QueryClient, key: readonly unknown[]): void {
  void client.invalidateQueries({ queryKey: key });
  void client.invalidateQueries({ queryKey: queryKeys.tags });
}

/**
 * Who is signed in, or nothing.
 *
 * The single source of truth for the session: the cookie is `httpOnly`, so this
 * request is the only thing that can answer the question.
 */
export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: api.me,
    // A 401 is an answer, not a failure to retry — the global policy in `main.tsx`
    // already declines to repeat anything below 500. This turns off the other half
    // of the same reasoning: by default a query left in error state asks again as
    // soon as another component starts observing it, and the guard redirecting to
    // the sign-in screen is exactly that second observer. Without this, every cold
    // visit asks who you are twice to be told the same thing.
    retryOnMount: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Reports an instance where nobody has created an account yet.
 *
 * Without it the sign-in screen refuses every attempt without ever saying that
 * there is simply nobody to sign in as, and a working installation looks broken
 * when one command is all that is missing.
 */
export function useSetupState() {
  return useQuery({
    queryKey: queryKeys.setupState,
    queryFn: api.setupState,
    staleTime: 60 * 1000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      api.login(username, password),
    // The response is the same `SessionUser` that `GET /api/auth/me` returns, so
    // seeding the cache with it spares the guard a round trip and the visitor a
    // spinner on the page they have just reached.
    onSuccess: (user) => queryClient.setQueryData(queryKeys.me, user),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.logout,
    // Empty the cache rather than only the session: everything in it was read
    // with the credentials that have just been given up, and on a shared device
    // the next person to sign in would otherwise see the previous one's data
    // until each query happened to refetch.
    //
    // The quick-capture buffer goes with it, and for the same reason — it is the
    // one thing in this application that survives on the device, and a note
    // written by one account must never be replayed into the next (D260815d).
    onSuccess: () => {
      queryClient.clear();
      clearOutbox();
    },
  });
}

/* ----------------------------------------------------------------------- Tags */

/**
 * Every tag this account has written, with what still carries each.
 *
 * One query for the whole application: the composer offers them for completion
 * and the counts appear beside them, and both read the same cache entry. Kept
 * fresh for a minute rather than on every mount — the registry changes only when
 * somebody writes a tag, and the mutations that could have done so invalidate it
 * themselves.
 */
export function useTags() {
  return useQuery({ queryKey: queryKeys.tags, queryFn: api.tags.list, staleTime: 60 * 1000 });
}

/* ---------------------------------------------------------------------- Todos */

/**
 * The whole list, in the order the server sorted it.
 *
 * One query for everything: the filters on screen are computed over what is
 * already here rather than asked for, so switching between Open, Today and All
 * costs nothing and cannot show one filter's data while another's is in flight.
 * A personal list is a few dozen rows — the round trip a server-side filter would
 * add is the entire cost of the feature.
 */
export function useTodos() {
  return useQuery({ queryKey: queryKeys.todos, queryFn: api.todos.list });
}

/**
 * What an optimistic mutation needs in order to be undone: the list exactly as it
 * was before the screen got ahead of the server.
 */
interface Rollback {
  previous: TodoList | undefined;
}

/**
 * Rewrites the cached list at once and returns the way back.
 *
 * Ticking a box is the most-used interaction on the screen, and a round trip
 * before the tick moves is felt. The same shape covers archiving, where the row
 * has to leave before a strip offering to restore it can make any sense.
 */
function writeList(client: QueryClient, change: (todos: Todo[]) => Todo[]): Rollback {
  const previous = client.getQueryData<TodoList>(queryKeys.todos);
  if (previous) client.setQueryData<TodoList>(queryKeys.todos, { todos: change(previous.todos) });
  return { previous };
}

/** The row as the server would return it, for the instant before it answers. */
function patched(todo: Todo, patch: UpdateTodoBody): Todo {
  return {
    ...todo,
    title: patch.title ?? todo.title,
    // Absent leaves the set alone; an empty array takes every tag off. The server
    // hands them back in alphabetical order and this does not sort — the refetch
    // that follows every mutation is what settles the order.
    tags: patch.tags ?? todo.tags,
    dueOn: patch.dueOn === undefined ? todo.dueOn : patch.dueOn,
    // The server keeps the first instant when a done todo is ticked again, and so
    // does this: otherwise the row would jump to the top of the Done section and
    // back down again when the answer arrived.
    doneAt:
      patch.done === undefined
        ? todo.doneAt
        : patch.done
          ? (todo.doneAt ?? new Date().toISOString())
          : null,
  };
}

export function useCreateTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.todos.create,
    // Not optimistic: the identifier is the server's to mint, and a row carrying a
    // temporary one cannot be ticked or archived until it has been replaced.
    onSuccess: () => invalidateWithTags(queryClient, queryKeys.todos),
  });
}

export function useUpdateTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateTodoBody }) =>
      api.todos.update(id, patch),
    onMutate: async ({ id, patch }) => {
      // A refetch already in flight would land after this and undo it on screen.
      await queryClient.cancelQueries({ queryKey: queryKeys.todos });
      return writeList(queryClient, (todos) =>
        todos.map((todo) => (todo.id === id ? patched(todo, patch) : todo)),
      );
    },
    onError: (_error, _variables, rollback) => {
      if (rollback?.previous) queryClient.setQueryData(queryKeys.todos, rollback.previous);
    },
    // On both paths: the server owns the ordering, and the optimistic list only
    // approximated it. The registry follows only when the patch touched the tags,
    // which is what keeps ticking a box to one request.
    onSettled: (_data, _error, variables) => {
      if (variables.patch.tags === undefined) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.todos });
      } else {
        invalidateWithTags(queryClient, queryKeys.todos);
      }
    },
  });
}

export function useArchiveTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.todos.archive,
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.todos });
      return writeList(queryClient, (todos) => todos.filter((todo) => todo.id !== id));
    },
    onError: (_error, _id, rollback) => {
      if (rollback?.previous) queryClient.setQueryData(queryKeys.todos, rollback.previous);
    },
    // The registry too: an archived row stops counting towards its tags, which is
    // the whole reason the counts are derived rather than stored.
    onSettled: () => invalidateWithTags(queryClient, queryKeys.todos),
  });
}

export function useRestoreTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.todos.restore,
    // Not optimistic either: the row left the cache when it was archived, and
    // putting it back in the right place is exactly what the refetch is for.
    onSuccess: () => invalidateWithTags(queryClient, queryKeys.todos),
  });
}

/* ------------------------------------------------------------------ Reminders */

/**
 * The whole list, in the order the server sorted it — the same shape as
 * `useTodos`, and optimistic in exactly the same places and for exactly the same
 * reasons: ticking and archiving yes, creating and restoring no.
 */
export function useReminders() {
  return useQuery({ queryKey: queryKeys.reminders, queryFn: api.reminders.list });
}

interface RemindersRollback {
  previous: ReminderList | undefined;
}

function writeReminders(
  client: QueryClient,
  change: (reminders: Reminder[]) => Reminder[],
): RemindersRollback {
  const previous = client.getQueryData<ReminderList>(queryKeys.reminders);
  if (previous) {
    client.setQueryData<ReminderList>(queryKeys.reminders, {
      reminders: change(previous.reminders),
    });
  }
  return { previous };
}

/** The row as the server would return it, for the instant before it answers. */
function patchedReminder(reminder: Reminder, patch: UpdateReminderBody): Reminder {
  return {
    ...reminder,
    title: patch.title ?? reminder.title,
    // Absent leaves the set alone; an empty array takes every tag off.
    tags: patch.tags ?? reminder.tags,
    remindAt: patch.remindAt ?? reminder.remindAt,
    doneAt:
      patch.done === undefined
        ? reminder.doneAt
        : patch.done
          ? (reminder.doneAt ?? new Date().toISOString())
          : null,
  };
}

export function useCreateReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.reminders.create,
    onSuccess: () => invalidateWithTags(queryClient, queryKeys.reminders),
  });
}

export function useUpdateReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateReminderBody }) =>
      api.reminders.update(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.reminders });
      return writeReminders(queryClient, (reminders) =>
        reminders.map((reminder) =>
          reminder.id === id ? patchedReminder(reminder, patch) : reminder,
        ),
      );
    },
    onError: (_error, _variables, rollback) => {
      if (rollback?.previous) queryClient.setQueryData(queryKeys.reminders, rollback.previous);
    },
    onSettled: (_data, _error, variables) => {
      if (variables.patch.tags === undefined) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.reminders });
      } else {
        invalidateWithTags(queryClient, queryKeys.reminders);
      }
    },
  });
}

export function useArchiveReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.reminders.archive,
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.reminders });
      return writeReminders(queryClient, (reminders) =>
        reminders.filter((reminder) => reminder.id !== id),
      );
    },
    onError: (_error, _id, rollback) => {
      if (rollback?.previous) queryClient.setQueryData(queryKeys.reminders, rollback.previous);
    },
    onSettled: () => invalidateWithTags(queryClient, queryKeys.reminders),
  });
}

export function useRestoreReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.reminders.restore,
    onSuccess: () => invalidateWithTags(queryClient, queryKeys.reminders),
  });
}

/* ---------------------------------------------------------------------- Notes */

/**
 * The list, as summaries. The bodies stay on the server until one is opened.
 *
 * One query for everything, as for the two lists above: the folder tree and the
 * tag facet are both computed over what has already arrived, so moving between
 * folders costs nothing.
 */
export function useNotes() {
  return useQuery({ queryKey: queryKeys.notes, queryFn: api.notes.list });
}

/** One note, body included. Its own key, so the list's churn does not reach it. */
export function useNote(id: string) {
  return useQuery({ queryKey: queryKeys.note(id), queryFn: () => api.notes.get(id) });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.notes.create,
    onSuccess: (note) => {
      // Seed the note's own entry with what came back, so opening the editor on
      // a note just created does not wait for a request whose answer is in hand.
      queryClient.setQueryData(queryKeys.note(note.id), note);
      invalidateWithTags(queryClient, queryKeys.notes);
    },
  });
}

/**
 * Saving a note, which happens on a timer rather than on a press.
 *
 * Not optimistic, and that is the difference from ticking a box: the editor
 * already shows what was typed, so there is nothing a cache write would put on
 * screen sooner. What the answer *is* good for is the title and excerpt the
 * server derived, which is why it is written straight into the note's entry.
 */
export function useUpdateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateNoteBody }) =>
      api.notes.update(id, patch),
    onSuccess: (note: Note) => {
      queryClient.setQueryData(queryKeys.note(note.id), note);
      if (note.tags.length > 0 || note.folderId !== null) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.folders });
      }
      invalidateWithTags(queryClient, queryKeys.notes);
    },
  });
}

export function useArchiveNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.notes.archive,
    onSuccess: (_ok, id) => {
      queryClient.removeQueries({ queryKey: queryKeys.note(id) });
      invalidateWithTags(queryClient, queryKeys.notes);
    },
  });
}

export function useRestoreNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.notes.restore,
    onSuccess: (note) => {
      queryClient.setQueryData(queryKeys.note(note.id), note);
      invalidateWithTags(queryClient, queryKeys.notes);
    },
  });
}

/* -------------------------------------------------------------------- Folders */

/**
 * The tree, flat and in path order, exactly as the server sorted it.
 *
 * `FolderTree` nests it for drawing; nothing re-sorts it. Kept fresh for a
 * minute rather than on every mount — a folder is created about as often as a
 * tag is invented.
 */
export function useFolders() {
  return useQuery({ queryKey: queryKeys.folders, queryFn: api.folders.list, staleTime: 60 * 1000 });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.folders.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.folders }),
  });
}

export function useUpdateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateFolderBody }) =>
      api.folders.update(id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.folders }),
  });
}

/**
 * The one mutation in this file that really deletes something.
 *
 * The list of notes follows it, because a folder cannot go while it still holds
 * one — so a deletion that succeeded is proof the counts beside it were right,
 * and a refusal means they were not.
 */
export function useDeleteFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.folders.remove,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.folders });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notes });
    },
  });
}

/* --------------------------------------------------------------------- Search */

/**
 * What matches, across every module.
 *
 * **`placeholderData: keepPreviousData` is the whole ergonomics of the palette.**
 * Without it every keystroke empties the list and refills it, and a list that
 * blinks on each letter is one nobody can read while typing. With it the previous
 * answer stays on screen, greyed, until the next arrives.
 *
 * Enabled from two characters, and again at zero: an empty query is not nothing
 * to ask, it is "what did I touch last", which is what the palette shows as it
 * opens. One character in between is a query that would match most of the
 * instance.
 *
 * `staleTime` is short rather than zero: reopening the palette on the same word a
 * few seconds later should not go back to the server, and a minute-old answer
 * about one's own notes is not wrong in any way that matters.
 */
export function useSearch(query: string, enabled: boolean) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: queryKeys.search(trimmed),
    queryFn: () => api.search.run(trimmed),
    enabled: enabled && (trimmed.length === 0 || trimmed.length >= 2),
    staleTime: 15 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * The instance's VAPID public key, which is what `PushManager.subscribe` needs.
 *
 * It is generated once and never changes, so this is cached for the life of the
 * tab rather than refetched: the only thing that could invalidate it is a
 * database somebody has replaced underneath the server.
 */
export function usePushKey() {
  return useQuery({
    queryKey: queryKeys.pushKey,
    queryFn: api.push.key,
    staleTime: Infinity,
    // A key that did not arrive means the toggle draws its "not available"
    // state, which is an answer. Retrying would only delay it.
    retry: false,
  });
}
